import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { EContactStatuses } from '@r1-frontend/api-domru/api-profile/v2/info/all/dto/ResponseDto';
import { ISuccessResponse } from '@r1-frontend/api-domru/phone-validator/validation/dto/ISuccessResponse';
import { TValidationType } from '@r1-frontend/api-domru/phone-validator/validation/dto/TValidationType';
import { send } from '@r1-frontend/api-domru/phone-validator/validation/send';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import BaseRadio from '@r1-frontend/ui-react/components/controls/baseRadio';
import DefaultPopup, { IDefaultPopup } from '@r1-frontend/ui-react/components/popups/defaultPopup';
import { IOption } from '@r1-frontend/ui-react/components/select/creatableSelect';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { useTimer } from '@r1-frontend/shared/hooks/useTimer';

import { TState } from '~/src/store';

import PhoneForm, { handleInputValue } from './PhoneForm';
import PhoneVerifyForm from './PhoneVerifyForm';
import PromoDurationBlock from './PromoDurationBlock';

import * as ST from './styled';

const inputPhoneLength = 11;
const defaultOption: IOption = { value: '', label: '' };

interface IVerifyTypeData {
    value: TValidationType,
    label: string,
}

export const verifyTypeData: IVerifyTypeData[] = [
    {
        value: 'sms',
        label: 'Пришлите смс',
    },
    {
        value: 'call',
        label: 'Позвоните мне',
    },
];

interface IProps extends IDefaultPopup {
    isConnectionInProgress: boolean,
    title: string,
    labelText: string,
    onConnect: (phone: IOption) => void,
}

const TryAndBuyConnectionPopup = ({
    isOpen,
    isConnectionInProgress,
    title,
    labelText,
    onConnect,
    onClose,
}: IProps): JSX.Element => {
    const { timeStatus, seconds, start, stop } = useTimer();

    const { clientContacts } = useSelector((state: TState) => state.auth, shallowEqual);

    const phoneOptions = useMemo(() => {
        const options: IOption[] = [];

        if (clientContacts?.phone.length) {
            clientContacts.phone.forEach(phone => {
                if (phone.status === EContactStatuses.confirmed) {
                    options.push({
                        value: phone.id,
                        label: phone.value,
                    });
                }
            });
        }

        return options;
    }, [clientContacts]);

    const [selectedPhone, setSelectedPhone] = useState<IOption>();
    const [enteredPhone, setEnteredPhone] = useState<IOption>(defaultOption);
    const [verifyType, setVerifyType] = useState<TValidationType>('sms');
    const [isFirstCodeRequest, setIsFirstCodeRequest] = useState(true);
    const [isCodeSended, setIsCodeSended] = useState(false);

    const onSelectPhone = (phoneOption: IOption) => {
        setSelectedPhone(phoneOption);

        if (phoneOption.value === handleInputValue) {
            return;
        }

        if (!isFirstCodeRequest) {
            setIsFirstCodeRequest(true);
        }

        if (enteredPhone.value) {
            setEnteredPhone(defaultOption);
        }
    };

    const onInputPhone = (phoneOption: IOption) => {
        setEnteredPhone(phoneOption);
    };

    const onSelectVerifyType = (type: string) => {
        if (type !== verifyType) {
            // @ts-ignore BaseRadio при onChange возвращает string
            setVerifyType(type);

            if (!isFirstCodeRequest) {
                setIsFirstCodeRequest(true);
            }
        }
    };

    const onConnectHandler = () => {
        const currentPhone = selectedPhone?.value === handleInputValue ? enteredPhone : selectedPhone;

        if (currentPhone) {
            onConnect(currentPhone);
        }
    };

    const onConfirmPhone = async() => {
        if (isFirstCodeRequest) {
            setIsFirstCodeRequest(false);
        }

        const resp = await doRequest.phoneValidator<ISuccessResponse>(
            send({
                phone: enteredPhone.value,
                type: verifyType,
            }),
        );

        if (resp.isSuccess) {
            setIsCodeSended(true);
        }
    };

    const onCloseHandler = () => {
        stop();
        setSelectedPhone(phoneOptions[0] ?? undefined);
        setEnteredPhone(defaultOption);
        setIsFirstCodeRequest(true);
        setIsCodeSended(false);
        onClose();
    };

    useEffect(() => {
        if (phoneOptions.length) {
            setSelectedPhone(phoneOptions[0]);
        }
    }, []);

    return (
        <DefaultPopup
            isOpen={isOpen}
            customStyles={ST.popupCustomStyles}
            onClose={onCloseHandler}
        >
            <ST.PopupContentWrapper>
                <H3 $withIndent={false}>{title}</H3>
                <ST.PhoneFormWrapper>
                    <PromoDurationBlock labelText={labelText}/>
                    <PhoneForm
                        options={phoneOptions}
                        value={selectedPhone}
                        onChange={onSelectPhone}
                        inputValue={enteredPhone.value}
                        createValue={handleInputValue}
                        createLabel={'Указать новый номер'}
                        onInputChange={onInputPhone}
                    />
                    {selectedPhone?.value === handleInputValue
                        ? <>
                            <Paragraph4 $smaller>Подтвердите ваш номер телефона любым удобным способом</Paragraph4>
                            <ST.VerifyTypesWrapper>
                                {verifyTypeData.map(type => (
                                    <BaseRadio
                                        key={type.value}
                                        size={'XS'}
                                        value={type.value}
                                        checked={type.value === verifyType}
                                        disabled={!!seconds}
                                        onChange={onSelectVerifyType}
                                    >
                                        {type.label}
                                    </BaseRadio>
                                ))}
                            </ST.VerifyTypesWrapper>
                            {isFirstCodeRequest
                                ? <Button
                                    size={'large'}
                                    bType={'action'}
                                    onClick={onConfirmPhone}
                                    disabled={(`${enteredPhone.value}`).length < inputPhoneLength}
                                >
                                    Подтвердить номер
                                </Button>
                                : <PhoneVerifyForm
                                    isCodeSended={isCodeSended}
                                    phone={enteredPhone.value}
                                    verifyType={verifyType}
                                    timeStatus={timeStatus}
                                    seconds={seconds}
                                    start={start}
                                    stop={stop}
                                    onSendSms={onConfirmPhone}
                                    onConfirmed={onConnectHandler}
                                />
                            }
                        </>
                        : <ButtonGroup>
                            <Button
                                size={'large'}
                                bType={'action'}
                                onClick={onConnectHandler}
                                loading={isConnectionInProgress}
                                disabled={!selectedPhone}
                            >
                                Подключить
                            </Button>
                        </ButtonGroup>
                    }
                </ST.PhoneFormWrapper>
            </ST.PopupContentWrapper>
        </DefaultPopup>
    );
};

export default TryAndBuyConnectionPopup;
